import { Box, Typography, Chip, keyframes } from "@mui/material";
import { AccessTime, Schedule, Update } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Lecture from "../../../models/lecture";
import { truncateString } from "../../../utils/truncate-string";
import { useState } from "react";
import { useSnackbar } from "../../../components/snackbar";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface LecturePreviewCardProps {
    lecture: Lecture;
    setJoining: React.Dispatch<React.SetStateAction<boolean>>;
}


export default function LecturePreviewCard({ lecture, setJoining }: LecturePreviewCardProps) {
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();

    const [url, setUrl] = useState<string | null>(null);
    
    const joinLecture = async (lecture: Lecture, status: string) => {
        try {
            if (status !== "live") {
                showMessage("Lecture is not live", 1);
                return;
            }

            setJoining(true);
            
            const response = await axios.get(
                `${API_URL}/student/classrooms/${lecture.classroom_id}/lectures/${lecture.id}/meeting-url`,
                { withCredentials: true }
            );

            if (response.data.url) {
                setUrl(response.data.url);
                window.open(response.data.url, '_blank');
            } else {
                showMessage(response.data.message, 1);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setJoining(false);
        }
    };
    
        const handleApiError = (error: any) => {
            if (error?.response?.status === 401) {
                navigate("/sign-in");
                return;
            }
    
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error.message ||
                "An unexpected error occurred";
    
            showMessage(message, 0);
        };

    // Helper function to determine lecture status
    const getLectureStatus = () => {
        if (!lecture.start_time || !lecture.duration) {
            return { status: 'scheduled', label: 'NO TIME SET', color: '#6b7280' };
        }
        
        const now = new Date();
        const startTime = new Date(lecture.start_time);
        const endTime = new Date(startTime.getTime() + (lecture.duration * 60 * 1000)); // duration in minutes to milliseconds
        
        if (now < startTime) {
            return { status: 'awaiting', label: 'AWAITING', color: '#f59e0b' };
        } else if (now >= startTime && now <= endTime) {
            return { status: 'live', label: 'LIVE', color: '#ef4444' };
        } else {
            return { status: 'ended', label: 'ENDED', color: '#6b7280' };
        }
    };

    // Helper function to format date time
    const formatDateTime = (dateString: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
            time: date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit'
            })
        };
    };

    const lectureStatus = getLectureStatus();
    const startTimeFormatted = lecture.start_time ? formatDateTime(lecture.start_time) : null;


    return (
        <Box
            sx={{
                padding: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.3s ease',
                backgroundColor: "white",
                border: "1px solid",
                borderColor: lectureStatus.status === 'live' 
                    ? "success.main" 
                    : lectureStatus.status === 'awaiting' 
                    ? "#f59e0b" 
                    : "grey.300",
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                },
                height: 80
            }}
            onClick={() => {
                joinLecture(lecture, lectureStatus.status);
            }}            
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography 
                        variant="subtitle2"
                        sx={{
                            fontWeight: 'bold',
                            marginBottom: '4px',
                            lineHeight: 1.2
                        }}
                    >
                        {truncateString(lecture.title, 25)}
                    </Typography>

                    {startTimeFormatted && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <AccessTime sx={{ fontSize: 12, color: '#64748b' }} />
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#64748b',
                                    fontSize: '0.7rem',
                                }}
                            >
                                {startTimeFormatted.date} at {startTimeFormatted.time}
                                {` • ${lecture.duration} minutes`}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1 }}>
                
                {url && (
                    <Chip
                        label="Copy Link"
                        size="small"
                        sx={{
                            borderRadius: 2,
                            backgroundColor: 'green',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 24,
                            '& .MuiChip-label': {
                                px: 1.5,
                            },
                        }}
                        onClick={() => {
                            navigator.clipboard.writeText(url)
                                .then(() => {
                                    showMessage('Join URL Copied!', 3);
                                })
                                .catch((err) => {
                                    console.error('Failed to copy URL:', err);
                                });
                        }}
                    />
                )}

                <Chip
                    label={lectureStatus.label}
                    size="small"
                    sx={{
                        backgroundColor: lectureStatus.status === 'live' ? '#fee2e2' : 
                                        lectureStatus.status === 'awaiting' ? '#fef3c7' : '#f3f4f6',
                        color: lectureStatus.color,
                        fontWeight: 600,
                        fontSize: '0.6rem',
                        height: 24,
                        '& .MuiChip-label': {
                            px: 1.5,
                        },
                        ...(lectureStatus.status === 'live' && {
                            animation: 'pulse 2s infinite'
                        })
                    }}
                />
            </Box>

        </Box>
    );
}